import { Component, LightComponent, Property, MeshComponent } from '@wonderlandengine/api';

// ─────────────────────────────────────────────────────────────────────────────
//  game-loop.js  —  DeadVox Game Loop (Phase 1)
//
//  State machine:  lobby → deploy → [scavenge → fortify → survive] × ∞
//                  Each night is harder. Only death ends the run.
//
//  Global side-effects:
//    window.GameState        'idle' | 'playing'   (read by stream.js + zombie-bot.js)
//    window.DeadVoxLoop      reference to this component instance
//
//  GameInterface calls (all optional/guarded):
//    .setPhase(phase, secondsLeft)    — HUD phase banner + timer
//    .setWave(killsRemaining, killTarget) — HUD kill counter
//    .pushConsoleLog(msg)             — log feed
//    .updateStats(hp, shield)         — triggers HUD health bar
//    .playerDie()                     — triggers death screen
//
//  Multiplayer messages (host → all peers):
//    { type: 'phase_change', phase, secondsLeft }
//    { type: 'wave_start',   waveNumber, totalWaves, killTarget }
//    { type: 'wave_complete',waveNumber }
//    { type: 'game_over',    reason }         reason: 'death'
// ─────────────────────────────────────────────────────────────────────────────

/** Duration (seconds) of each day phase. */
const PHASE_DURATIONS = {
    deploy:   0,    // brief countdown before the world opens
    scavenge: 120,  // 2 min of daylight mining
    fortify:  40,   // 40 s of dusk fortification
    extract:  30,   // 30 s extraction window after all waves cleared
    // survive: driven by wave completion, not a fixed timer
};

/**
 * Build the wave table for a night.
 * Nights 1–2: single wave. Every 3 nights an additional wave is added (max 4).
 * Later waves in the same night have more zombies and a 20 s break between them.
 */
function buildWaveTable(nightLevel) {
    const waves   = Math.min(1 + Math.floor(nightLevel / 3), 4);
    const perWave = Math.ceil((nightLevel + 1) / waves);
    const table   = [];
    for (let i = 0; i < waves; i++) {
        table.push({
            killTarget:    perWave + i * 2,
            spawnCount:    perWave + i * 2,
            breakDuration: i < waves - 1 ? 20 : 0,
        });
    }
    return table;
}

// ─────────────────────────────────────────────────────────────────────────────

export class GameLoop extends Component {
    static TypeName = 'game-loop';

    static Properties = {
        /** Speed up phase timers for testing. 1.0 = normal, 0.1 = 10× faster, 0.02 = 50× faster. */
        debugTimeScale: Property.float(1.0),
        /**
         * Auto-cycle mode: skip zombie AI, auto-kill the whole wave each frame,
         * run for 100 nights then stop. Combine with a low debugTimeScale (e.g. 0.02)
         * to stress-test scaling tables quickly.
         */
        debugAutoCycle: Property.bool(false),
        /** How many waves compose a full run (0 = use WAVE_TABLE length). */
        totalWaves: Property.int(0),
        /**
         * One of your existing zombie scene objects.
         * GameLoop will clone it at runtime to grow the pool as needed.
         * Leave unset to rely only on manually placed scene bots.
         */
        zombiePrefab: Property.object(),
        /** Directional sun light — driven by day/night cycle. */
        sun: Property.object(),
        /** Player flashlight — brightens at dusk and night. */
        starsObject: Property.object(),
        flashlight: Property.object(),
        /** Sky/background material — color lerped through day/dusk/night. */
        skyMaterial: Property.material(),
        /** Terrain fog material — color lerped to black at night. */
        fogMaterial: Property.material(),
        /** Foliage/tree material — fog color matched to sky bottom. */
        foliageMaterial: Property.material(),
        /** Zombie mesh material — fog color matched to sky bottom. */
        zombieMaterial: Property.material(),
    };

    // ── Lifecycle ─────────────────────────────────────────────────────────────

    start() {
        window.DeadVoxLoop = this;
        this.stars = this.starsObject?.getComponent(MeshComponent);
        if (this.stars) this.stars.active = false;
        // Expose a one-shot start method the HUD lobby button can call.
        window.GameInterface = window.GameInterface || {};
        window.GameInterface.startRun = (cx, cz, seed) => this._startRun(cx, cz, seed);
        window.GameInterface.endRun   = () => this._endRun('death');

        // Night counter — how many nights survived this session
        this._nightLevel = 0;

        this._spawnCX = null;
        this._spawnCZ = null;

        // Build the wave table for night 1
        this._waveTable  = buildWaveTable(this._nightLevel);

        // Phase state
        this._phase       = 'lobby';
        this._phaseTimer  = 0;
        this._totalWaves  = this.totalWaves > 0 ? this.totalWaves : this._waveTable.length;

        // Wave state
        this._waveNumber  = 0;   // 0 = pre-waves, 1-N = active wave index
        this._waveKills   = 0;
        this._waveBreakTimer = 0;
        this._inWaveBreak = false;

        // Lighting targets (lerped each frame)
        this._sunTarget   = 2.0;
        this._flashTarget = 20.0 ;
        // Sky color target [r, g, b, a]
        this._skyTarget   = [0.3, 0.55, 1.0, 1.0];

        // Capture the sky material's initial gradient stop colors as the "day" reference.
        // These are read once here and stored — phase targets lerp between them and black.
        this._skyDay = null;
        if (this.skyMaterial) {
            try {
                this._skyDay = [
                    Array.from(this.skyMaterial.colorStop0 ?? [0,0,0,1]),
                    Array.from(this.skyMaterial.colorStop1 ?? [0,0,0,1]),
                    Array.from(this.skyMaterial.colorStop2 ?? [0,0,0,1]),
                    Array.from(this.skyMaterial.colorStop3 ?? [0,0,0,1]),
                ];
                // Targets start at day
                this._skyStopTargets = this._skyDay.map(c => [...c]);
            } catch (_) {}
        }

        // Capture fog material — prefer the editor property, otherwise pull the
        // terrain base voxel material from stream.js (window.VoxelTreadmill._baseVoxelMaterial)
        this._fogDay    = null;
        this._fogTarget = null;
        this._initFogMaterial();

        // Intercept GameState so setting it to 'playing' triggers phase lighting
        let _gameState = 'idle';
        Object.defineProperty(window, 'GameState', {
            configurable: true,
            get: () => _gameState,
            set: (val) => {
                _gameState = val;
                if (val === 'playing') this._setLightingForPhase(this._phase);
            },
        });

        // Set idle until the player starts
        window.GameState = 'idle';

        // Monkey-patch ZombieBot._die to count kills without modifying zombie-bot.js
        this._patchZombieDie();

        // Expose message handler for the multiplayer system
        if (window.OasisMultiplayer) {
            window.OasisMultiplayer.onData?.((msg) => this.syncState(msg));
        }

        console.log('[GameLoop] Initialised — call window.GameInterface.startRun() to begin');
    }

    update(dt) {
        const scaled = dt / (this.debugTimeScale || 1.0);

        // Smoothly lerp light intensities toward their phase targets
        this._lerpLights(dt);

        // Dawn transition delay: 3 s pause between survive → scavenge
        if (this._dawnPending) {
            this._dawnTimer -= scaled;
            if (this._dawnTimer <= 0) {
                this._dawnPending = false;
                this._lerpSpeed   = 1.2;
                this._enterPhase('scavenge');
            }
        }

        switch (this._phase) {
            case 'deploy':   this._tickDeploy(scaled);   break;
            case 'scavenge': this._tickScavenge(scaled);  break;
            case 'fortify':  this._tickFortify(scaled);   break;
            case 'survive':  this._tickSurvive(scaled);   this._ensureKillPatch(); break;
            case 'extract':  this._tickExtract(scaled);   break;
            default: break; // lobby / ended — nothing to tick
        }

        // Periodic state broadcast for host to prevent drift and update late-joiners
        if (this._isHost() && window.GameState === 'playing') {
            this._stateSyncTimer = (this._stateSyncTimer || 0) - dt;
            if (this._stateSyncTimer <= 0) {
                this._stateSyncTimer = 2.0; // sync every 2 seconds
                this._broadcastState();
            }
        }
    }

    // ── Phase transitions ─────────────────────────────────────────────────────

    _startRun(cx, cz, seed) {
        if (this._phase !== 'lobby') return;

        this._nightLevel = 0;
        this._totalKills = 0;
        this._waveTable  = buildWaveTable(this._nightLevel);
        this._totalWaves = this.totalWaves > 0 ? this.totalWaves : this._waveTable.length;

        // Apply any stored meta-progression upgrades
        this._applyMetaUpgrades();

        // Notify the UI to hide menus/enter XR
        window.GameInterface?.onRunStarted?.();

        const vt = window.VoxelTreadmill;
        if (!vt) { this._enterPhase('deploy'); return; }

        // Determine run type:
        //   joiner   — host explicitly provided cx/cz/seed via network sync
        //   resume   — saved world exists (same seed + spawn every time)
        //   newWorld — first time ever, generate a fresh seed and spawn
        const isJoiner  = cx != null && cz != null && seed != null;
        const hasSaved  = vt._savedSpawnCX != null;

        const _doTeleport = (spawnCX, spawnCZ, remote = false) => {
            const cs      = vt.chunkSize || 16;
            const spawnVX = spawnCX * cs;
            const spawnVZ = spawnCZ * cs;
            vt.object.setPositionWorld([-spawnVX, -100, -spawnVZ]);
            vt._playerCX = null; vt._playerCZ = null;
            vt._updateWorld(spawnCX, spawnCZ);
            this._enterPhase('deploy', remote);
        };

        if (isJoiner) {
            // Adopt host's world — wipe in-memory state and sync to host's seed/spawn.
            // clearForJoin preserves our worldMeta so solo play after disconnect resumes
            // our own persistent world, not the host's.
            vt.setSeed?.(seed);
            vt.clearForJoin?.().then(() => {
                const mp = window.OasisMultiplayer;
                if (mp && !mp._isHost && mp._hostMutations) {
                    try { vt.deserializeModifications(mp._hostMutations); } catch (_) {}
                    mp._hostMutations = null;
                }
                this._spawnCX = cx;
                this._spawnCZ = cz;
                _doTeleport(cx, cz, true); // remote=true: suppress re-broadcast to host/peers
            }).catch(() => this._enterPhase('deploy', true));

        } else if (hasSaved) {
            // Resume the persistent world — mutations and saved position already in DB.
            // Player stats were reset by resetPlayerStats() in _endRun on death;
            // on a fresh session load, loadPlayerState() already restored them.
            this._spawnCX = vt._savedSpawnCX;
            this._spawnCZ = vt._savedSpawnCZ;
            // When hosting multiplayer, the host must start at spawn, not at their last
            // solo-mode position. Block _pendingPositionRestore (which loadPlayerState
            // sets on page load) so it doesn't fire in update() and override the teleport.
            const mp = window.OasisMultiplayer;
            if (mp && mp._isHost) {
                vt._pendingPositionRestore = null;
                vt._blockPositionRestore   = true;
            }
            _doTeleport(this._spawnCX, this._spawnCZ);

        } else {
            // First time — create the persistent world with a new random seed and spawn.
            const finalSeed = Math.floor(Math.random() * 1000000);
            vt.setSeed?.(finalSeed);
            this._spawnCX = Math.floor(Math.random() * 201) - 100;
            this._spawnCZ = Math.floor(Math.random() * 201) - 100;
            vt.clearAllData?.().then(() => {
                vt.saveWorldSpawn?.(this._spawnCX, this._spawnCZ);
                _doTeleport(this._spawnCX, this._spawnCZ);
            }).catch(() => this._enterPhase('deploy'));
        }
    }

    _enterPhase(phase, remote = false) {
        this._phase      = phase;
        this._phaseTimer = (PHASE_DURATIONS[phase] ?? 0) * (this.debugTimeScale || 1.0);

        this._setLightingForPhase(phase);

        const gi = window.GameInterface;

        switch (phase) {
            case 'deploy':
                window.GameState = 'idle'; // keep input frozen during deploy countdown
                if (!remote) this._broadcast({ type: 'phase_change', phase: 'deploy', secondsLeft: Math.ceil(this._phaseTimer), noiseSeed: window.VoxelTreadmill?.noiseSeed, spawnCX: this._spawnCX, spawnCZ: this._spawnCZ });
                gi?.setPhase?.('deploy', Math.ceil(this._phaseTimer));
                gi?.pushConsoleLog?.('DEPLOYING - PREPARE FOR DROP');
                break;

            case 'scavenge':
                window.GameState = 'playing';
                if (!remote) this._broadcast({ type: 'phase_change', phase: 'scavenge', secondsLeft: Math.ceil(this._phaseTimer) });
                gi?.setPhase?.('scavenge', Math.ceil(this._phaseTimer));
                gi?.pushConsoleLog?.('DAYLIGHT - MINE RESOURCES, EXPLORE BIOMES');
                break;

            case 'fortify':
                window.GameState = 'playing';
                if (!remote) this._broadcast({ type: 'phase_change', phase: 'fortify', secondsLeft: Math.ceil(this._phaseTimer) });
                gi?.setPhase?.('fortify', Math.ceil(this._phaseTimer));
                gi?.pushConsoleLog?.('DUSK - BUILD YOUR DEFENSES NOW');
                break;

            case 'survive':
                window.GameState = 'playing';
                this._waveNumber      = 0;
                this._waveKills       = 0;
                this._inWaveBreak     = false;
                if (!remote) this._broadcast({ type: 'phase_change', phase: 'survive', secondsLeft: -1 });
                gi?.setPhase?.('survive', -1);
                gi?.pushConsoleLog?.(`NIGHT ${this._nightLevel + 1} - THE HORDE IS COMING`);
                this._startNextWave();
                break;
            
            case 'extract':
                window.GameState = 'playing';
                if (!remote) this._broadcast({ type: 'phase_change', phase: 'extract', secondsLeft: Math.ceil(this._phaseTimer) });
                gi?.setPhase?.('extract', Math.ceil(this._phaseTimer));
                gi?.pushConsoleLog?.('EXTRACTION PORTAL ACTIVE - EVACUATE NOW');
                break;

            case 'ended':
                window.GameState = 'idle';
                break;
        }
    }

    // ── Per-phase tick methods ────────────────────────────────────────────────

    _tickDeploy(dt) {
        this._phaseTimer -= dt;
        window.GameInterface?.setPhase?.('deploy', Math.max(0, Math.ceil(this._phaseTimer)));
        if (this._phaseTimer <= 0) this._enterPhase('scavenge');
    }

    _tickScavenge(dt) {
        this._phaseTimer -= dt;
        window.GameInterface?.setPhase?.('scavenge', Math.max(0, Math.ceil(this._phaseTimer)));

        // Blend sky from dawn palette → noon palette over the first 25% of scavenge
        const total  = PHASE_DURATIONS.scavenge;
        const elapsed = total - Math.max(0, this._phaseTimer);
        const t      = Math.min(elapsed / (total * 0.25), 1); // 0→1 in first 25%
        if (this._skyDay && this._skyPalette) {
            const dawn = this._skyPalette.dawn;
            const noon = this._skyPalette.noon;
            this._skyStopTargets = dawn.map((dc, i) => dc.map((v, ch) => v + (noon[i][ch] - v) * t));
            // Fog: dawn warm haze → day
            if (this._fogDay && this._fogPalette) {
                const fd = this._fogPalette.dawn;
                this._fogTarget = fd.map((v, ch) => v + (this._fogDay[ch] - v) * t);
            }
            // Sun: dawn orange warmth → white noon
            const ds = this._sunPalette.dawn;
            const ns = this._sunPalette.noon;
            this._sunColorTarget = ds.map((v, ch) => v + (ns[ch] - v) * t);
        }

        if (this._phaseTimer <= 0) this._enterPhase('fortify');
    }

    _tickFortify(dt) {
        this._phaseTimer -= dt;
        window.GameInterface?.setPhase?.('fortify', Math.max(0, Math.ceil(this._phaseTimer)));

        // Blend sky from golden hour → twilight over the fortify phase
        const total  = PHASE_DURATIONS.fortify;
        const elapsed = total - Math.max(0, this._phaseTimer);
        const t      = Math.min(elapsed / total, 1);
        if (this._skyPalette) {
            const gold   = this._skyPalette.golden;
            const twi    = this._skyPalette.twilight;
            this._skyStopTargets = gold.map((gc, i) => gc.map((v, ch) => v + (twi[i][ch] - v) * t));
            if (this._fogPalette) {
                const fgold = this._fogPalette.golden;
                const ftwi  = this._fogPalette.twilight;
                this._fogTarget = fgold.map((v, ch) => v + (ftwi[ch] - v) * t);
            }
            const gs = this._sunPalette.golden;
            const ts = this._sunPalette.twilight;
            this._sunColorTarget = gs.map((v, ch) => v + (ts[ch] - v) * t);
        }

        if (this._phaseTimer <= 0) this._enterPhase('survive');
    }

    _tickSurvive(dt) {
        if (!this._isHost() || this._dawnPending) return;

        // ── Wave break countdown ───────────────────────────────────────────────
        if (this._inWaveBreak) {
            this._waveBreakTimer -= dt;
            if (this._waveBreakTimer <= 0) {
                this._inWaveBreak = false;
                this._startNextWave();
            }
            return;
        }

        // ── Debug auto-cycle: instantly rack up kills each frame ──────────────
        if (this.debugAutoCycle) {
            const wave = this._waveTable[this._waveNumber - 1];
            if (wave) this._waveKills = wave.killTarget; // complete wave this tick
        }

        // ── Active wave: check kill target ────────────────────────────────────
        const wave = this._waveTable[this._waveNumber - 1];
        if (wave && this._waveKills >= wave.killTarget) {
            this._completeWave();
        }
    }

    _tickExtract(dt) {
        if (!this._isHost()) return;
        this._phaseTimer -= dt;
        window.GameInterface?.setPhase?.('extract', Math.max(0, Math.ceil(this._phaseTimer)));
        if (this._phaseTimer <= 0) {
            // Time ran out — force extraction anyway (roguelite: never softlock)
            this._endRun('extracted');
        }
    }

    // ── Wave management ───────────────────────────────────────────────────────

    _startNextWave() {
        this._waveNumber++;
        this._waveKills = 0;

        const wave = this._waveTable[this._waveNumber - 1];
        if (!wave) {
            // All waves survived — dawn breaks, next night will be harder
            this._nightSurvived();
            return;
        }

        window.GameInterface?.setWave?.(0, wave.killTarget);
        window.GameInterface?.pushConsoleLog?.(
            `NIGHT ${this._nightLevel + 1} - KILL ${wave.killTarget} ZOMBIES`
        );

        if (this._isHost()) this._broadcast({
            type: 'wave_start',
            waveNumber: this._waveNumber,
            totalWaves: this._totalWaves,
            killTarget: wave.killTarget,
        });

        // Only the host manages zombie spawns/AI
        if (this._isHost()) this._activateZombiesForWave(wave.spawnCount);
    }

    _completeWave() {
        const wave = this._waveTable[this._waveNumber - 1];

        this._broadcast({ type: 'wave_complete', waveNumber: this._waveNumber });
        window.GameInterface?.pushConsoleLog?.(`WAVE ${this._waveNumber} CLEARED`);

        if (this._waveNumber >= this._totalWaves) {
            this._nightSurvived();
            return;
        }

        // Brief break before next wave
        this._inWaveBreak     = true;
        this._waveBreakTimer  = (wave?.breakDuration ?? 12);
        window.GameInterface?.setPhase?.('survive', Math.ceil(this._waveBreakTimer));
        window.GameInterface?.pushConsoleLog?.(
            `NEXT WAVE IN ${Math.ceil(this._waveBreakTimer)}s`
        );
    }

    /** All waves cleared — increment night, rebuild table, return to scavenge. */
    _nightSurvived(remote = false) {
        // Debug auto-cycle: stop after 100 nights and print the full scaling report
        if (this.debugAutoCycle && this._nightLevel >= 99) {
            console.table(
                Array.from({ length: 100 }, (_, i) => {
                    const t = buildWaveTable(i);
                    return {
                        night: i + 1,
                        waves: t.length,
                        killsWave1: t[0].killTarget,
                        killsLast:  t[t.length - 1].killTarget,
                        spawnLast:  t[t.length - 1].spawnCount,
                    };
                })
            );
            console.log('[GameLoop] debugAutoCycle complete — 100 nights simulated');
            this._enterPhase('ended');
            return;
        }

        this._nightLevel++;
        this._waveTable  = buildWaveTable(this._nightLevel);
        this._totalWaves = this.totalWaves > 0 ? this.totalWaves : this._waveTable.length;

        // Persist best night reached
        try {
            const meta = JSON.parse(localStorage.getItem('deadvox_meta') || '{}');
            meta.nightLevel = Math.max(meta.nightLevel || 0, this._nightLevel);
            localStorage.setItem('deadvox_meta', JSON.stringify(meta));
        } catch (_) {}

        if (!remote) this._broadcast({ type: 'night_survived', nightLevel: this._nightLevel });
        window.GameInterface?.pushConsoleLog?.(
            `DAWN - NIGHT SURVIVED! NEXT NIGHT: ${this._waveTable.length} WAVES x${(1 + this._nightLevel * 0.2).toFixed(1)}`
        );
        window.GameInterface?.setWave?.(null, null);

        // Deactivate and hide all bots — they must not carry over into the next wave
        const bots = window.ZombieBots;
        if (bots) for (const z of bots) {
            z._waveFreeze = true;
            z._active     = false;
            z._dead       = true;
            z.object?.setPositionLocal?.(z._hidePos ?? [0, -1000, 0]);
        }

        // Return to daytime — skip delay in debug auto-cycle mode
        if (this.debugAutoCycle) {
            this._enterPhase('scavenge');
        } else {
            // Begin lighting dawn transition immediately with a slow lerp,
            // but delay the actual phase change by 3 seconds for a smooth sunrise feel.
            this._lerpSpeed = 0.3;
            this._setLightingForPhase('scavenge');
            this._dawnPending = true;
            this._dawnTimer   = 3.0;
        }
    }

    /**
     * Grow the ZombieBots pool to at least `minSize` by cloning zombiePrefab.
     * Clones are frozen (waveFreeze=true) and registered into window.ZombieBots.
     * Handles two registration patterns:
     *   A) ZombieBot.start() pushes itself during clone() — already in array.
     *   B) ZombieBot.start() doesn't fire yet — we grab the component manually.
     */
    _ensurePool(minSize) {
        if (!this.zombiePrefab) {
            console.warn('[GameLoop] zombiePrefab not assigned — cannot grow pool');
            return;
        }

        window.ZombieBots = window.ZombieBots || [];
        const needed = minSize - window.ZombieBots.length;
        if (needed <= 0) return;

        console.log(`[GameLoop] Cloning ${needed} zombie(s) to reach pool size ${minSize}`);

        // Grab the player reference from the blueprint so clones inherit it.
        const ZombieBotClass = window.ZombieBotClass;
        const blueprintComp  = ZombieBotClass
            ? this.zombiePrefab.getComponent(ZombieBotClass)
            : this.zombiePrefab.getComponent('zombie-bot');
        const blueprintPlayer = blueprintComp?.player ?? null;

        for (let i = 0; i < needed; i++) {
            try {
                const beforeLen = window.ZombieBots.length;
                const cloneObj  = this.zombiePrefab.clone();

                // Parent to the VoxelTreadmill scene OBJECT so getPositionLocal == grid coords.
                const vtObj = window.VoxelTreadmill?.object;
                if (vtObj) cloneObj.parent = vtObj;

                // Pattern A: ZombieBot.start() fired synchronously and self-registered
                if (window.ZombieBots.length > beforeLen) {
                    const bot = window.ZombieBots[window.ZombieBots.length - 1];
                    bot._waveFreeze = true;
                    bot._active     = false;
                    if (blueprintPlayer && !bot.player) bot.player = blueprintPlayer;
                    continue;
                }

                // Pattern B: start() hasn't fired yet — flag the object so start() skips
                // the state reset and skips self-registration when it eventually runs.
                cloneObj._dvClone = true;

                const comp = ZombieBotClass
                    ? cloneObj.getComponent(ZombieBotClass)
                    : cloneObj.getComponent('zombie-bot');

                if (comp) {
                    comp._waveFreeze = true;
                    comp._active     = false;
                    if (blueprintPlayer && !comp.player) comp.player = blueprintPlayer;
                    window.ZombieBots.push(comp);
                } else {
                    // Last resort: store the object and let zombie-bot register later
                    cloneObj._dvClone       = true;
                    cloneObj._dvWaveFreeze  = true;
                    console.warn('[GameLoop] clone registered late — bot will self-freeze on start()');
                }
            } catch (e) {
                console.warn('[GameLoop] zombie clone failed:', e);
                break;
            }
        }
    }

    /**
     * Activate N bots for the current wave.
     * Grows the pool via cloning if not enough inactive bots exist.
     */
    _activateZombiesForWave(count) {
        // Hard-reset every bot so surviving zombies from the previous wave
        // don't stack on top of this wave's spawn budget.
        const existing = window.ZombieBots || [];
        for (const z of existing) {
            z._waveFreeze = true;
            z._active     = false;
            z._dead       = true;
            z.object?.setPositionLocal?.(z._hidePos ?? [0, -1000, 0]);
        }

        // Expose the concurrent cap so zombie respawn can self-limit.
        this._waveSpawnCount = count;

        // Grow the pool if we don't have enough bots yet.
        if (existing.length < count) this._ensurePool(count);

        let spawned = 0;
        const allBots = window.ZombieBots || [];
        for (const z of allBots) {
            if (spawned >= count) break;
            z._waveFreeze = false;
            const delay = spawned === 0 ? 0 : Math.floor(Math.random() * 2500);
            if (delay === 0) {
                z.activate();
            } else {
                setTimeout(() => { if (!z._waveFreeze) z.activate(); }, delay);
            }
            spawned++;
        }
    }

    // ── End of run ────────────────────────────────────────────────────────────

    _endRun(reason) {
        if (this._phase === 'ended') return;

        this._enterPhase('ended');

        const gi = window.GameInterface;
        gi?.setPhase?.('ended', 0);

        this._broadcast({ type: 'game_over', reason });

        // Freeze + deactivate all bots and bump the run token so any
        // pending tryRespawn2 timers from this run self-cancel immediately.
        window.DeadVoxRunToken = (window.DeadVoxRunToken || 0) + 1;
        const bots = window.ZombieBots;
        if (bots) {
            for (const z of bots) {
                z._active     = false;
                z._waveFreeze = true;
                z._dead       = true;
                z.object?.setPositionWorld?.([0, -1000, 0]);
            }
        }

        // Save meta-progression to localStorage
        this._saveProgress(reason);

        if (reason === 'death') {
            gi?.playerDie?.();
            gi?.pushConsoleLog?.(`YOU DIED ON NIGHT ${this._nightLevel + 1} - NIGHTS SURVIVED: ${this._nightLevel}`);
        } else {
            gi?.pushConsoleLog?.('EXTRACTION COMPLETE - RETURNING TO HUB');
        }

        // Unblock player movement on all clients immediately.
        try { window.VoxelTreadmill?.respawnPlayer?.(); } catch (_) {}

        // Only the host auto-restarts. Non-hosts receive the new run via the
        // phase_change broadcast triggered when the host calls _startRun.
        if (this._isHost()) {
            // Reset player vitals now so the world persists but the player
            // starts the next run with clean stats.
            const vt = window.VoxelTreadmill;
            if (vt) {
                vt.resetPlayerStats?.();
            }
            setTimeout(() => {
                this._phase         = 'lobby';
                this._nightLevel    = 0;
                this._waveNumber    = 0;
                this._waveKills     = 0;
                this._totalKills    = 0;
                this._inWaveBreak   = false;
                this._waveBreakTimer = 0;
                window.GameState    = 'idle';
                this._startRun();
            }, 5500);
        }
    }

    // ── Meta-progression (localStorage) ───────────────────────────────────────

    _applyMetaUpgrades() {
        try {
            const meta = JSON.parse(localStorage.getItem('deadvox_meta') || '{}');
            const vt   = window.VoxelTreadmill;

            if (vt) {
                // Upgraded starting HP (100 → 125 → 150)
                const hpBonus = (meta.startHpLevel || 0) * 25;
                vt._health = 100 + hpBonus;
                vt._shield = 100 + (meta.shieldLevel || 0) * 8;
                window.GameInterface?.updateStats?.(vt._health, vt._shield);
            }

            // Gun damage multiplier exposed for fps-pistol to read
            window.DeadVoxMeta = meta;
        } catch (_) {}
    }

    _saveProgress(reason) {
        try {
            const vt   = window.VoxelTreadmill;
            const meta = JSON.parse(localStorage.getItem('deadvox_meta') || '{}');

            // Collect resource value from inventory
            const inv = vt?._inventory ?? {};
            const value = (inv.alloy       || 0) * 1
                        + (inv.biomass    || 0) * 2
                        + (inv.titanium   || 0) * 4
                        + (inv.plasma     || 0) * 6
                        + (inv.crystal    || 0) * 8
                        + (inv.shieldCells|| 0) * 5
                        + (inv.medkits    || 0) * 6;

            // Died mid-run: keep 25% of carried resources as shards
            const kept  = Math.floor(value * 0.25);
            meta.shards = (meta.shards || 0) + kept;

            // Run record
            meta.lastRun = {
                nightsCleared:  this._nightLevel,
                zombiesKilled:  this._totalKills || 0,
                shardsGained:   kept,
                outcome:        reason,
            };

            localStorage.setItem('deadvox_meta', JSON.stringify(meta));

            if (kept > 0) window.GameInterface?.pushConsoleLog?.(`+${kept} SHARDS SALVAGED`);
        } catch (_) {}
    }

    // ── Zombie kill counter (monkey-patch, no zombie-bot.js edits needed) ─────

    _patchZombieDie() {
        // Attempt to patch the prototype; retries until bots register.
        const tryPatch = () => {
            const bots = window.ZombieBots;
            if (!bots?.length) { setTimeout(tryPatch, 500); return; }
            this._applyKillPatch(bots[0]);
            for (const z of bots) z._waveFreeze = true;
        };
        setTimeout(tryPatch, 2000);
    }

    /**
     * Fallback called every tick during survive phase.
     * If new bots appeared since last patch (e.g. clones), re-apply patch.
     */
    _ensureKillPatch() {
        const bots = window.ZombieBots;
        if (!bots?.length) return;
        const proto = Object.getPrototypeOf(bots[0]);
        if (proto && !proto._dvPatched) this._applyKillPatch(bots[0]);
    }

    _applyKillPatch(sampleBot) {
        // Only the host processes zombie death logic to prevent double counting
        if (!this._isHost()) return;

        const proto = Object.getPrototypeOf(sampleBot);
        if (!proto || proto._dvPatched) return;
        const originalDie = proto._die;
        if (typeof originalDie !== 'function') return;
        const loop = this;
        proto._die = function (...args) {
            loop._onZombieKilled(this);
            return originalDie.apply(this, args);
        };
        proto._dvPatched = true;
        console.log('[GameLoop] zombie kill patch applied');
    }

    _onZombieKilled(bot) {
        if (!this._isHost()) return;

        this._totalKills = (this._totalKills || 0) + 1;

        if (this._phase !== 'survive' || this._inWaveBreak) {
            if (bot) bot._waveFreeze = true;
            return;
        }

        this._waveKills++;

        const wave = this._waveTable[this._waveNumber - 1];
        if (wave) {
            const remaining = Math.max(0, wave.killTarget - this._waveKills);
            window.GameInterface?.setWave?.(wave.killTarget - remaining, wave.killTarget);
            window.GameInterface?.pushConsoleLog?.(
                `KILL ${this._waveKills}/${wave.killTarget} - ${remaining} LEFT`
            );
        }
        
        // Sync kill progress to peers
        this._broadcast({ type: 'wave_kills', kills: this._waveKills, totalKills: this._totalKills });

        if (this._waveNumber > this._totalWaves) {
            if (bot) bot._waveFreeze = true;
        }
    }

    _broadcastState() {
        this._broadcast({
            type: 'game_state',
            nightLevel:  this._nightLevel,
            phase:       this._phase,
            phaseTimer:  this._phaseTimer,
            waveNumber:  this._waveNumber,
            waveKills:   this._waveKills,
            totalKills:  this._totalKills,
            spawnCX:     this._spawnCX,
            spawnCZ:     this._spawnCZ,
            noiseSeed:   window.VoxelTreadmill?.noiseSeed,
        });
    }

    syncState(msg) {
        if (!msg) return;
        const gi = window.GameInterface;

        switch (msg.type) {
            case 'game_state':
                // JOINER joining mid-game: bootstrap the world before entering any phase.
                // _joinerStarting guards against duplicate calls during the async clearAllData gap.
                if (this._phase === 'lobby' && !this._isHost() && !this._joinerStarting && msg.phase !== 'lobby') {
                    this._joinerStarting = true;
                    const mp = window.OasisMultiplayer;
                    // Prefer live broadcast values; fall back to welcome snapshot
                    const cx   = msg.spawnCX   ?? mp?._hostSpawnCX ?? null;
                    const cz   = msg.spawnCZ   ?? mp?._hostSpawnCZ ?? null;
                    const seed = msg.noiseSeed ?? mp?._hostSeed    ?? null;
                    this._startRun(cx, cz, seed);
                    // Subsequent game_state messages (after _phase leaves 'lobby') will sync the rest.
                    break;
                }

                this._nightLevel = msg.nightLevel;
                if (this._phase !== msg.phase) this._enterPhase(msg.phase, true);
                this._phaseTimer = msg.phaseTimer;
                this._waveNumber = msg.waveNumber;
                this._waveKills  = msg.waveKills;
                this._totalKills = msg.totalKills;

                gi?.setPhase?.(this._phase, Math.ceil(this._phaseTimer));
                const wave = this._waveTable[this._waveNumber - 1];
                if (wave) gi?.setWave?.(this._waveKills, wave.killTarget);

                /* JOINER: Ensure pool is large enough to receive state for clones */
                if (!this._isHost() && wave) {
                    this._ensurePool(wave.spawnCount);
                }
                break;
            case 'phase_change':
                if (msg.phase === 'deploy' && !this._isHost()) {
                    this._phase = 'lobby';
                    this._joinerStarting = false;
                    // New run starting on host — reset joiner state and re-join with new seed/spawn.
                    // Works for fresh joiners (phase=lobby) and returning ones (phase=ended).
                    this._phase = 'lobby';
                    this._joinerStarting = false;
                    this._startRun(msg.spawnCX ?? null, msg.spawnCZ ?? null, msg.noiseSeed ?? null);
                } else if (msg.phase !== 'deploy') {
                    if (this._phase !== msg.phase) this._enterPhase(msg.phase, true);
                    if (msg.secondsLeft > 0) this._phaseTimer = msg.secondsLeft;
                }
                break;
            case 'wave_start':
                this._waveNumber = msg.waveNumber;
                this._waveKills  = 0;
                gi?.setWave?.(0, msg.killTarget);
                gi?.pushConsoleLog?.(`NIGHT ${this._nightLevel + 1} - KILL ${msg.killTarget} ZOMBIES`);
                break;
            case 'wave_complete':
                gi?.pushConsoleLog?.(`WAVE ${msg.waveNumber} CLEARED`);
                break;
            case 'wave_kills':
                this._waveKills = msg.kills;
                this._totalKills = msg.totalKills;
                const currentWave = this._waveTable[this._waveNumber - 1];
                if (currentWave) gi?.setWave?.(this._waveKills, currentWave.killTarget);
                break;
            case 'night_survived':
                this._nightSurvived(true);
                break;
            case 'game_over':
                this._endRun(msg.reason);
                break;
            case 'player_died':
                // If any player dies, the run ends globally on the host
                if (this._isHost()) this._endRun('death');
                break;
        }
    }

    // ── Lighting ──────────────────────────────────────────────────────────────

    /**
     * Resolve the fog material from stream.js (window.VoxelTreadmill._baseVoxelMaterial)
     * or fall back to the editor-assigned fogMaterial property.
     * Retries every 500 ms until VoxelTreadmill is ready.
     */
    _initFogMaterial() {
        let _fogRetries = 0;
        const tryInit = () => {
            if (++_fogRetries > 20) { console.warn('[GameLoop] fog material not found after 10s — giving up'); return; }
            const mat = this.fogMaterial
                     ?? window.VoxelTreadmill?._baseVoxelMaterial
                     ?? null;
            if (!mat) { setTimeout(tryInit, 500); return; }

            try {
                const fc = mat.fogColor;
                if (fc) {
                    this._fogDay    = Array.from(fc);
                    this._fogTarget = Array.from(fc);
                    // Patch fogMaterial ref so _lerpLights can write to it
                    this.fogMaterial = this.fogMaterial ?? mat;
                    console.log('[GameLoop] fog material resolved from', mat);
                }
            } catch (_) {}
        };
        setTimeout(tryInit, 500);
    }

    /**
     * Map each phase to target sun and flashlight intensities.
     *   deploy   → dawn — red-orange horizon, warm haze
     *   scavenge → morning → noon (blended over time in _tickScavenge)
     *   fortify  → golden hour → twilight (blended over time in _tickFortify)
     *   survive  → deep night — cold blue-black sky, flashlight on
     *   ended    → everything off
     */
    _setLightingForPhase(phase) {
        // ── Sky colour palettes — each is 4 gradient stops [r,g,b,a] ─────────
        //  stop0 = horizon/bottom, stop1 = lower mid, stop2 = upper mid, stop3 = top of sky
        const P = {
            // Dawn: coral sunrise over a cyan ocean horizon
            dawn: [
                [0.08, 0.32, 0.55, 1],  // bottom  — deep ocean blue
                [0.14, 0.50, 0.70, 1],  // low-mid — teal-cyan
                [0.14, 0.50, 0.70, 1],
                [0.08, 0.32, 0.55, 1],
            ],
            // Noon: vivid open-ocean cyan
            noon: [
                [0.10, 0.55, 0.76, 1],  // bottom  — bright cyan sea
                [0.14, 0.65, 0.84, 1],  // low-mid — vivid ocean
                [0.14, 0.65, 0.84, 1],
                [0.10, 0.55, 0.76, 1],
            ],
            // Golden hour: warm aqua — ocean gold as sun dips
            golden: [
                [0.06, 0.36, 0.62, 1],  // bottom  — deep teal
                [0.10, 0.52, 0.72, 1],  // low-mid — warm cyan
                [0.10, 0.52, 0.72, 1],
                [0.06, 0.36, 0.62, 1],
            ],
            // Twilight: deep ocean dusk
            twilight: [
                [0.04, 0.18, 0.38, 1],  // bottom  — dark navy
                [0.06, 0.26, 0.50, 1],  // low-mid — deep ocean blue
                [0.06, 0.26, 0.50, 1],
                [0.04, 0.18, 0.38, 1],
            ],
            // Night: absolute pitch black
            night: [
                [0.00, 0.00, 0.00, 1],
                [0.00, 0.00, 0.00, 1],
                [0.00, 0.00, 0.00, 1],
                [0.00, 0.00, 0.00, 1],
            ],
        };
        this._skyPalette = P;

        // ── Fog palettes — matched to sky bottom stop, cyan ocean blue theme ──
        const F = {
            dawn:     [0.08, 0.32, 0.55, 0.1],
            noon:     [0.10, 0.55, 0.76, 0.1],
            golden:   [0.06, 0.36, 0.62, 0.1],
            twilight: [0.04, 0.18, 0.38, 0.1],
            night:    [0.00, 0.00, 0.00, 0.1],
        };
        this._fogPalette = F;
        // Override _fogDay so scavenge's dawn→noon blend targets our cyan noon,
        // not whatever colour was baked into the voxel material in the editor.
        this._fogDay = [...F.noon];

        // ── Sun light colour targets (tints the directional light) ────────────
        const S = {
            dawn:     [1.00, 0.72, 0.45, 1],  // coral sunrise over ocean
            noon:     [1.00, 0.98, 0.90, 1],  // neutral warm white
            golden:   [1.00, 0.80, 0.38, 1],  // warm amber-gold
            twilight: [0.35, 0.60, 0.82, 1],  // cool ocean blue twilight
            night:    [0.00, 0.00, 0.00, 1],  // flashlight only
        };
        this._sunPalette = S;

        // ── Intensity table ───────────────────────────────────────────────────
        const sunFlash = {
            lobby:    { sun: 0.8,  flash: 0.0  },
            deploy:   { sun: 0.6,  flash: 0.5  },  // pre-dawn, hint of flash
            scavenge: { sun: 1.4,  flash: 0.0  },  // bright day
            fortify:  { sun: 0.5,  flash: 4.0  },  // golden hour dimming
            survive:  { sun: 0.0,  flash: 22.0 },  // full night
            ended:    { sun: 0.8,  flash: 0.0  },  // back to lobby day
        };
        const sf = sunFlash[phase] ?? sunFlash.lobby;
        this._sunTarget   = sf.sun;
        this._flashTarget = sf.flash;

        if (this.stars) {
            this.stars.active = (phase === 'fortify' || phase === 'survive');
        }

        // ── Set initial sky stop targets per phase ────────────────────────────
        switch (phase) {
            case 'lobby':
            case 'deploy':
                // Dawn colours — will start blending to noon once scavenge ticks
                this._skyStopTargets = P.dawn.map(c => [...c]);
                this._fogTarget      = [...F.dawn];
                this._sunColorTarget = [...S.dawn];
                break;

            case 'scavenge':
                // Start at dawn, _tickScavenge blends toward noon
                this._skyStopTargets = P.dawn.map(c => [...c]);
                this._fogTarget      = [...F.dawn];
                this._sunColorTarget = [...S.dawn];
                break;

            case 'fortify':
                // Start at golden hour, _tickFortify blends to twilight
                this._skyStopTargets = P.golden.map(c => [...c]);
                this._fogTarget      = [...F.golden];
                this._sunColorTarget = [...S.golden];
                break;

            case 'survive':
                this._skyStopTargets = P.night.map(c => [...c]);
                this._fogTarget      = [...F.night];
                this._sunColorTarget = [...S.night];
                break;

            case 'ended':
                // Game over — return sky to dawn so the world is lit on respawn
                this._skyStopTargets = P.dawn.map(c => [...c]);
                this._fogTarget      = [...F.dawn];
                this._sunColorTarget = [...S.dawn];
                break;

            default:
                this._skyStopTargets = P.noon.map(c => [...c]);
                this._fogTarget      = this._fogDay ? [...this._fogDay] : [...F.noon];
                this._sunColorTarget = [...S.noon];
        }

        // Pin fog alpha to 0.1 on every daytime update so materials are always correct.
        for (const mat of [this.fogMaterial, this.foliageMaterial, this.zombieMaterial]) {
            if (!mat) continue;
            const c = mat.fogColor;
            if (!c) continue;
            c[3] = 0.1;
            mat.fogColor = c;
        }
    }
    /** Lerp current light intensities, colours, and sky gradient stops toward their targets. */
    _lerpLights(dt) {
        const t = Math.min((this._lerpSpeed ?? 1.2) * dt, 1);

        if (this.sun) {
            const light = this.sun.getComponent(LightComponent);
            if (light) {
                light.intensity += (this._sunTarget - light.intensity) * t;
                // Tint sun color if the material supports it
                if (this._sunColorTarget && typeof light.color !== 'undefined') {
                    const c = light.color;
                    if (c) {
                        c[0] += (this._sunColorTarget[0] - c[0]) * t;
                        c[1] += (this._sunColorTarget[1] - c[1]) * t;
                        c[2] += (this._sunColorTarget[2] - c[2]) * t;
                        light.color = c;
                    }
                }
            }
        }

        if (this.flashlight) {
            // The assigned object may be the parent — search it and its children
            const light = this.flashlight.getComponent(LightComponent)
                       ?? this.flashlight.children
                              .map(c => c.getComponent(LightComponent))
                              .find(Boolean);
            if (light) light.intensity += (this._flashTarget - light.intensity) * t;
        }

        if (this.skyMaterial && this._skyStopTargets) {
            const stops = ['colorStop0', 'colorStop1', 'colorStop2', 'colorStop3'];
            for (let i = 0; i < 4; i++) {
                const c = this.skyMaterial[stops[i]];
                const tgt = this._skyStopTargets[i];
                if (!c || !tgt) continue;
                c[0] += (tgt[0] - c[0]) * t;
                c[1] += (tgt[1] - c[1]) * t;
                c[2] += (tgt[2] - c[2]) * t;
                c[3] += (tgt[3] - c[3]) * t;
                this.skyMaterial[stops[i]] = c;
            }
        }

        if (this._fogTarget) {
            for (const mat of [this.fogMaterial, this.foliageMaterial, this.zombieMaterial]) {
                if (!mat) continue;
                const c = mat.fogColor;
                if (!c) continue;
                c[0] += (this._fogTarget[0] - c[0]) * t;
                c[1] += (this._fogTarget[1] - c[1]) * t;
                c[2] += (this._fogTarget[2] - c[2]) * t;
                c[3] += (this._fogTarget[3] - c[3]) * t;
                mat.fogColor = c;
            }
        }
    }

    // ── Helpers ───────────────────────────────────────────────────────────────

    _isHost() {
        const mp = window.OasisMultiplayer;
        if (!mp) return true; // no multiplayer module — solo
        if (mp._isHost) return true;
        // If we have open data connections but _isHost is false, we joined as a client.
        // Don't fall back to remotePositions — it's 0 until the first position packet
        // arrives, which causes phase_change:deploy to be silently ignored on the first run.
        if ((mp._dataConns?.size ?? 0) > 0) return false;
        // Solo fallback: no peers connected means we should run zombie AI locally
        return (mp._remotePositions?.size ?? 0) === 0;
    }

    _broadcast(msg) {
        try { window.OasisMultiplayer?._broadcast?.(msg); } catch (_) {}
    }

    // ── Public API (called by HUD buttons or multiplayer messages) ────────────

    /** Called by GameInterface.extract() when player stands in portal. */
    notifyExtract() {
        if (this._phase !== 'extract') return;
        this._endRun('extracted');
    }

    /** Returns current run stats for the HUD. */
    getRunStats() {
        return {
            phase:       this._phase,
            nightLevel:  this._nightLevel,
            wave:        this._waveNumber,
            totalWaves:  this._totalWaves,
            waveKills:   this._waveKills,
            totalKills:  this._totalKills || 0,
        };
    }
}
